import apiClient from './config';
import type { Task } from '@/types/task';
import type { Project } from '@/types/project';

export type GlobalSearchTab = 'all' | 'tasks' | 'projects';

interface GlobalSearchBaseParams {
  keyword?: string;
  current?: number;
  pageSize?: number;
}

interface GlobalSearchTaskParams extends GlobalSearchBaseParams {
  projectId?: string;
  frameworkName?: string;
  status?: 'in_progress' | 'completed';
  assigneeId?: string;
}

interface ApiSuccess<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface SearchRequestOptions {
  signal?: AbortSignal;
}

export interface GlobalSearchAllData {
  tasks: Task[];
  projects: Project[];
  tasksTotal: number;
  projectsTotal: number;
}

export interface GlobalSearchListData<T> {
  data: T[];
  total: number;
  hasMore: boolean;
}

async function getByTab<T>(
  tab: GlobalSearchTab,
  params: GlobalSearchBaseParams & {
    projectId?: string;
    frameworkName?: string;
    status?: string;
    assigneeId?: string;
  },
  options: SearchRequestOptions = {}
): Promise<ApiSuccess<T>> {
  return (await apiClient.get('/search/global', {
    params: {
      tab,
      keyword: params.keyword,
      current: params.current,
      pageSize: params.pageSize,
      projectId: params.projectId,
      frameworkName: params.frameworkName,
      status: params.status,
      assigneeId: params.assigneeId,
    },
    signal: options.signal,
  })) as ApiSuccess<T>;
}

function buildError(message: string): Error {
  return new Error(message);
}

export async function getGlobalSearchAll(
  params: Pick<GlobalSearchBaseParams, 'keyword'>,
  options: SearchRequestOptions = {}
): Promise<GlobalSearchAllData> {
  const response = await getByTab<GlobalSearchAllData>('all', params, options);
  if (!response.success || !response.data) {
    throw buildError(response.error || '加载全局搜索摘要失败');
  }
  return response.data;
}

export async function getGlobalSearchTasks(
  params: GlobalSearchTaskParams,
  options: SearchRequestOptions = {}
): Promise<GlobalSearchListData<Task>> {
  const response = await getByTab<Task[]>('tasks', params, options);
  const typed = response as ApiSuccess<Task[]> & {
    total?: number;
    hasMore?: boolean;
  };

  if (!typed.success || !typed.data) {
    throw buildError(typed.error || '加载任务搜索结果失败');
  }

  return {
    data: typed.data,
    total: typed.total || 0,
    hasMore: Boolean(typed.hasMore),
  };
}

export async function getGlobalSearchProjects(
  params: GlobalSearchBaseParams,
  options: SearchRequestOptions = {}
): Promise<GlobalSearchListData<Project>> {
  const response = await getByTab<Project[]>('projects', params, options);
  const typed = response as ApiSuccess<Project[]> & {
    total?: number;
    hasMore?: boolean;
  };

  if (!typed.success || !typed.data) {
    throw buildError(typed.error || '加载项目搜索结果失败');
  }

  return {
    data: typed.data,
    total: typed.total || 0,
    hasMore: Boolean(typed.hasMore),
  };
}
